import { Slot, Slottable } from "@radix-ui/react-slot";
import clsx from "clsx";
import * as React from "react";

interface BaseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  startIcon?: React.ReactElement;
  endIcon?: React.ReactElement;
  className?: string;
  fullWidth?: boolean;
  classNames?: {
    root?: string;
    startIcon?: string;
    endIcon?: string;
  };
}

// Variant and Color combinations
type FilledPrimaryButtonProps = {
  variant: "primary";
  color: "primary";
};

type LinkButtonProps = {
  variant?: "link" | "link-underline";
  color?: "primary" | "gray" | "blue";
};

type OtherButtonProps = {
  variant?: "secondary" | "tertiary";
  color?: "primary" | "gray";
};

// Merging the types
type ButtonProps = BaseButtonProps &
  (FilledPrimaryButtonProps | OtherButtonProps | LinkButtonProps);

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      classNames,
      size = "md",
      children,
      variant = "primary",
      color = "primary",
      fullWidth = false,
      startIcon,
      asChild,
      endIcon,
      ...rest
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        data-size={size}
        data-variant={variant}
        data-color={color}
        data-fullwidth={fullWidth}
        className={clsx(
          "ox-reset",
          "ox-ui-Button",
          className,
          classNames?.root,
        )}
        {...rest}
      >
        {startIcon ? (
          <span className={clsx("ox-Button-iconSlot", classNames?.startIcon)}>
            {startIcon}
          </span>
        ) : null}
        <Slottable>{children}</Slottable>
        {endIcon ? (
          <span className={clsx("ox-Button-iconSlot", classNames?.endIcon)}>
            {endIcon}
          </span>
        ) : null}
      </Comp>
    );
  },
);
Button.displayName = "Button";
export { Button };
export type { ButtonProps };
